import {
  inquiryApi,
  InquiryCounts,
  InquiryVersionSlot,
  InquiryWithContext,
} from '@texas/api/endpoints/inquiryApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useVersionOverviewContext } from '@texas/components/article/variant/versions/shared/VersionOverviewContext';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { ReactSelectOption } from '@texas/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { inquiryEvents } from './events';
import { InquiryLifecycle } from './InquiryLifecycleTabs';
import { useFilterInquiries } from './useFilterInquiries';
import { reactEvents } from '@bridge/reactEvents';

const EMPTY_VERSIONS: InquiryVersionSlot[] = [];
const EMPTY_INQUIRIES: InquiryWithContext[] = [];
const EMPTY_COUNTS: InquiryCounts = {
  open: 0,
  sent: 0,
  pricesReceived: 0,
  accepted: 0,
  archived: 0,
};

interface UseInquiriesOptions {
  variantId: number;
  mode: 'matrix' | 'non-matrix';
  fixedSupplierIds?: number[];
  initialLifecycle?: InquiryLifecycle;
}

export function useInquiries({
  variantId,
  mode,
  fixedSupplierIds,
  initialLifecycle,
}: UseInquiriesOptions) {
  const { filters, updateFilter } = useVersionOverviewContext();
  const {
    useCurrenciesData: { findCurrency },
    currenciesData: [, refetchCurrencies],
  } = useMetadata();
  const {
    data: pageData,
    loading,
    error,
    refetch,
    set,
  } = useApiResource(inquiryApi.getByVariant);
  const { data: countData, refetch: refetchCounts } = useApiResource(
    inquiryApi.getCountsByVariant,
  );
  const { request: createRequest } = useApiRequest(inquiryApi.createInquiry);
  const [searchInput, setSearchInput] = useState('');
  const [displayCurrencyId, setDisplayCurrencyId] = useState<number | null>(
    null,
  );
  const [activeLifecycle, setActiveLifecycle] = useState<InquiryLifecycle>(
    initialLifecycle ?? 'all',
  );

  const versions = pageData?.versions ?? EMPTY_VERSIONS;
  const inquiries = pageData?.inquiries ?? EMPTY_INQUIRIES;
  const hasLoadedPageData = pageData !== null || error !== null;
  const countFilters = useMemo(
    () => ({
      variantId,
      supplierIds: fixedSupplierIds ?? filters.supplierIds,
      optionIds: filters.optionIds,
      branchIds: filters.branchIds,
      cellX: filters.cellX,
      cellY: filters.cellY,
      inquiryNumber: filters.inquiryNumber,
      isController: mode === 'matrix',
    }),
    [
      filters.branchIds,
      filters.cellX,
      filters.cellY,
      filters.inquiryNumber,
      filters.optionIds,
      filters.supplierIds,
      fixedSupplierIds,
      mode,
      variantId,
    ],
  );

  useEffect(() => {
    return inquiryEvents.onTierPricePatched.subscribe(({ tierId, price }) => {
      set((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          inquiries: previous.inquiries.map((inquiry) => ({
            ...inquiry,
            tierPrices: inquiry.tierPrices.map((tierPrice) =>
              tierPrice.id === tierId ? { ...tierPrice, price } : tierPrice,
            ),
          })),
        };
      });
    });
  }, [set]);

  useEffect(() => {
    refetchCurrencies();
  }, [refetchCurrencies]);

  useEffect(() => {
    refetch({
      variantId,
      ...filters,
      ...(fixedSupplierIds && { supplierIds: fixedSupplierIds }),
    });
  }, [refetch, variantId, filters, fixedSupplierIds]);

  useEffect(() => {
    refetchCounts(countFilters);
  }, [countFilters, refetchCounts]);

  const refetchAll = useCallback(() => {
    refetch({
      variantId,
      ...filters,
      ...(fixedSupplierIds && { supplierIds: fixedSupplierIds }),
    });
    refetchCounts(countFilters);
  }, [
    countFilters,
    filters,
    fixedSupplierIds,
    refetch,
    refetchCounts,
    variantId,
  ]);

  useEffect(() => {
    const unsubscribe = inquiryEvents.onVariantUpdated.subscribe(
      (updatedVariantId) => {
        if (updatedVariantId === variantId) refetchAll();
      },
    );
    const unsubscribeLegacy = inquiryEvents.onUpdated.subscribe(refetchAll);
    return () => {
      unsubscribe();
      unsubscribeLegacy();
    };
  }, [refetchAll, variantId]);

  const createInquiry = useCallback(
    async (compositionGroupPurchaseInfoId: number) => {
      const response = await createRequest(compositionGroupPurchaseInfoId);
      if (!response.hasError)
        inquiryEvents.onVariantUpdated.dispatch(variantId);
    },
    [createRequest, variantId],
  );

  const { controllerVersionGroups } = useFilterInquiries({
    mode,
    versions,
    inquiries,
  });

  const allSlots = useMemo(
    () => Array.from(controllerVersionGroups.values()).flat(),
    [controllerVersionGroups],
  );

  const createSlots = useMemo(
    () => allSlots.filter((slot) => slot.canCreateInquiry),
    [allSlots],
  );

  const displayCurrencyOptions = useMemo<ReactSelectOption[]>(() => {
    const optionsByCode = new Map<string, ReactSelectOption>();
    for (const { currencyId } of inquiries) {
      const currency = findCurrency(currencyId);
      const key = currency?.code ?? `id:${currencyId}`;
      if (!optionsByCode.has(key) || currencyId === displayCurrencyId) {
        optionsByCode.set(key, {
          value: currencyId,
          label: currency?.code ?? String(currencyId),
        });
      }
    }
    return Array.from(optionsByCode.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [displayCurrencyId, findCurrency, inquiries]);

  const lifecycleCounts = countData ?? EMPTY_COUNTS;

  const listInquiries = useMemo(
    () =>
      inquiries.filter(
        (inquiry) =>
          inquiry.inquiryControlledById === null &&
          (mode === 'matrix' ? inquiry.isController : !inquiry.isController),
      ),
    [inquiries, mode],
  );

  const displayedInquiries = useMemo(() => {
    switch (activeLifecycle) {
      case 'all':
        return listInquiries;
      case 'archived':
        return listInquiries.filter((inquiry) => inquiry.archived);
      case 'accepted':
        return listInquiries.filter(
          (inquiry) => !inquiry.archived && inquiry.acceptedDate,
        );
      case 'sent':
        return listInquiries.filter(
          (inquiry) =>
            !inquiry.archived &&
            inquiry.sent &&
            !inquiry.acceptedDate &&
            inquiry.priceCount === 0,
        );
      case 'prices-received':
        return listInquiries.filter(
          (inquiry) =>
            !inquiry.archived &&
            inquiry.sent &&
            !inquiry.acceptedDate &&
            inquiry.priceCount > 0,
        );
      case 'open':
        return listInquiries.filter(
          (inquiry) =>
            !inquiry.archived && !inquiry.sent && !inquiry.acceptedDate,
        );
    }
  }, [activeLifecycle, listInquiries]);

  const tableSlots = useMemo(() => {
    const visibleSlotIds = new Set(
      displayedInquiries.map(
        (inquiry) => inquiry.compositionGroupPurchaseInfoId,
      ),
    );

    if (activeLifecycle === 'open') {
      const occupiedSlotIds = new Set(
        listInquiries.map((inquiry) => inquiry.compositionGroupPurchaseInfoId),
      );
      return allSlots.filter(
        (slot) =>
          visibleSlotIds.has(slot.compositionGroupPurchaseInfoId) ||
          !occupiedSlotIds.has(slot.compositionGroupPurchaseInfoId),
      );
    }

    return allSlots.filter((slot) =>
      visibleSlotIds.has(slot.compositionGroupPurchaseInfoId),
    );
  }, [activeLifecycle, allSlots, displayedInquiries, listInquiries]);

  const tierColumns = useMemo(() => {
    const quantities = new Set<number>();
    inquiries.forEach((inquiry) => {
      inquiry.tierPrices.forEach((tierPrice) =>
        quantities.add(tierPrice.quantity),
      );
      inquiry.allTargetTiers.forEach((targetTier) =>
        quantities.add(targetTier.quantity),
      );
    });
    return Array.from(quantities).sort((a, b) => a - b);
  }, [inquiries]);

  const updateLifecycle = useCallback(
    (value: InquiryLifecycle) => {
      setActiveLifecycle(value);
      updateFilter('showArchived', value === 'archived' || value === 'all');
    },
    [updateFilter],
  );

  useEffect(() => {
    return reactEvents.routingChanged.sub(({ to, navObj, newTab }) => {
      if (
        newTab ||
        to !== 'variant.inquiry' ||
        navObj?.status !== 'all' ||
        (navObj?.variantId != null && Number(navObj.variantId) !== variantId)
      ) {
        return;
      }
      updateLifecycle('all');
    });
  }, [updateLifecycle, variantId]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      updateFilter('inquiryNumber', value.trim() || undefined);
    },
    [updateFilter],
  );

  return {
    activeLifecycle,
    allSlots,
    createSlots,
    createInquiry,
    displayCurrencyId,
    displayCurrencyOptions,
    displayedInquiries,
    error,
    handleSearchChange,
    hasLoadedPageData,
    lifecycleCounts,
    loading,
    searchInput,
    setDisplayCurrencyId,
    tableSlots,
    tierColumns,
    updateLifecycle,
    versions,
  };
}
